import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './AdminFeedback.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';


// Delegates to the shared helper so the active-entity header (X-RiskLo-Entity)
// rides along. Building the Authorization header locally silently dropped it,
// which made every admin screen query Vector no matter which brand was selected.
function authHeaders() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

export default function AdminVectorNtMappings({ onNavigate }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState('');
  const [ntName, setNtName] = useState('');
  const [canonical, setCanonical] = useState('');
  const [csvText, setCsvText] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/vector-nt-strategy-mappings`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load');
      setMappings(data.mappings || []);
    } catch (e) {
      setError(e.message);
      setMappings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const apiBase = `${API_BASE_URL}/api/admin/vector-nt-strategy-mappings`;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!ntName.trim() || !canonical.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          account_name: account.trim(),
          nt_strategy_name: ntName.trim(),
          canonical_strategy_name: canonical.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setNtName('');
      setCanonical('');
      setMessage('Mapping saved. Use “Re-apply to stored trades” if you need existing rows updated.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiBase}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReapply = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiBase}/reapply`, { method: 'POST', headers: authHeaders(), body: '{}' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Re-apply failed');
      setMessage(data.message || 'Done.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImportCsv = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiBase}/import-csv`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Import failed');
      setMessage(`Imported ${data.rowsImported} row(s) and re-applied mappings.`);
      setCsvText('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-feedback-page">
      <div className="admin-feedback-content">
        <div className="admin-feedback-header">
          <h1 className="admin-feedback-title">Vector add-on strategy names</h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', maxWidth: '720px', lineHeight: 1.5 }}>
            Map NinjaTrader account + the raw strategy label NT sends (e.g. <code>Gfb2.2</code> on{' '}
            <code>Sim101</code>) to the exact RiskLo / spreadsheet name (e.g.{' '}
            <code>Tuesday Special (Previous 2.2A)</code>). Account-specific rows win over a row with an empty
            account (applies to all accounts). Matching is case-insensitive on the NT name.
          </p>
          <button
            type="button"
            className="admin-feedback-modal-btn admin-feedback-modal-btn-secondary"
            style={{ marginTop: '0.75rem' }}
            onClick={() => onNavigate && onNavigate('admin-feedback')}
          >
            Back to Feedback Admin
          </button>
        </div>

        {error && (
          <div className="admin-feedback-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: '1rem', color: 'rgba(34,197,94,0.95)' }}>{message}</div>
        )}

        <form onSubmit={handleAdd} style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>Add or update mapping</h2>
          <div style={{ display: 'grid', gap: '0.75rem', maxWidth: '560px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>NT account (optional)</span>
              <input
                className="admin-feedback-search"
                placeholder="Sim101 — leave empty for all accounts"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>NT strategy name (as sent by the add-on)</span>
              <input
                className="admin-feedback-search"
                placeholder="Gfb2.2"
                value={ntName}
                onChange={(e) => setNtName(e.target.value)}
                required
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>RiskLo / spreadsheet name</span>
              <input
                className="admin-feedback-search"
                placeholder="Tuesday Special (Previous 2.2A)"
                value={canonical}
                onChange={(e) => setCanonical(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="admin-feedback-bulk-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save mapping'}
            </button>
          </div>
        </form>

        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Bulk CSV import</h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            Three columns per line: <code>account_name,nt_strategy_name,canonical_strategy_name</code>.
            Empty account = all accounts. Optional header row with “account” is skipped.
          </p>
          <textarea
            className="admin-feedback-modal-notes"
            style={{ width: '100%', maxWidth: '640px', minHeight: '120px', fontFamily: 'monospace' }}
            placeholder={'Sim101,Gfb2.2,Tuesday Special (Previous 2.2A)\nSim201,PrimeBeef,PrimeBeef'}
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
          <button
            type="button"
            className="admin-feedback-bulk-btn"
            style={{ marginTop: '0.5rem' }}
            disabled={saving || !csvText.trim()}
            onClick={handleImportCsv}
          >
            Import CSV &amp; re-apply
          </button>
        </div>

        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="admin-feedback-bulk-btn" disabled={saving} onClick={handleReapply}>
            Re-apply mappings to stored trades
          </button>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
            Run after editing mappings so existing database rows pick up new canonical names.
          </span>
        </div>

        {loading ? (
          <p className="admin-feedback-loading">Loading…</p>
        ) : (
          <div className="admin-feedback-table-wrap admin-vector-nt-mappings-wrap">
            <table className="admin-feedback-table">
              <thead>
                <tr>
                  <th>NT account</th>
                  <th>NT strategy name</th>
                  <th>RiskLo name</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {mappings.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="admin-feedback-empty">
                      No mappings yet.
                    </td>
                  </tr>
                ) : (
                  mappings.map((m) => (
                    <tr key={m.id}>
                      <td>{m.account_name || '— (all)'}</td>
                      <td>{m.nt_strategy_name}</td>
                      <td>{m.canonical_strategy_name}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-feedback-save-btn"
                          onClick={() => handleDelete(m.id)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
